import React, { useMemo, useRef, useEffect, useState } from 'react';
import { StyleSheet, Animated, View, Platform, Easing } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { useUIStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import { MESSAGE_LAYOUT, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import PetWaitingIcon from '@/components/Pets/PetWaitingIcon';
import PropTypes from 'prop-types';

// Rotating phrases for waiting state (fallbacks when no status is available)
const WAITING_PHRASES = [
  'Starting your request…',
  'Planning next steps…',
  'Gathering information…',
  'Preparing response…',
  'Drafting response…',
  'Processing results…',
];

// Stage fallback status messages (same as in MessageThinkingContent)
const STATUS_STAGE_FALLBACKS = {
  start: 'Starting your request…',
  intent: 'Planning next steps…',
  preprocess: 'Gathering information…',
  preparation: 'Preparing response…',
  execution: 'Drafting response…',
  extraction: 'Processing results…',
  heartbeat: 'Working on your request…',
  unknown: 'Working on your request…',
};

const getStageFallback = (stage) => {
  const normalizedStage = (stage || '').trim().toLowerCase();
  if (!normalizedStage) return '';
  return STATUS_STAGE_FALLBACKS[normalizedStage] || STATUS_STAGE_FALLBACKS.unknown;
};

const isNative = Platform.OS !== 'web';

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────

const createStyles = (colors, isXS, messageWidth) => StyleSheet.create({
  // Layout containers (shared between web + native)
  bubbleContainer: {
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
    maxWidth: 
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW : 
      messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : '100%',
    alignSelf: 'center',
    paddingHorizontal: 16,
  },
  bubbleContainerInner: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: colors.solid_color_tertiary,
    marginBottom: 0,
  },
  bubble: {
    padding: 16,
    borderRadius: 12,
    alignSelf: 'flex-start',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 64,
    minHeight: 40,
    backgroundColor: colors.background_secondary
  },

  slimContainer: {
    paddingVertical: 8,
    paddingHorizontal: 0,
    position: 'relative',
    alignSelf: 'center',
    width: 
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW : 
      messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : '100%',
    maxWidth: '100%',
  },
  slimContainerInner: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
  },
  slimContent: {
    flex: 1,
  },
  slimContentWrapper: {
    flex: 1,
    flexShrink: 1,
  },
  slimAvatar: {
    width: isXS ? 20 : 30,
    height: isXS ? 20 : 30,
    borderRadius: 99,
    marginRight: isXS ? 8 : 12,
    color: colors.text_color_tertiary,
    backgroundColor: colors.solid_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  slimContentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
  },
  slimContentUsername: {
    marginRight: 8,
  },
  
  wideContainer: {
    flexDirection: 'row',
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 0,
    alignSelf: 'center',
    width: 
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW : 
      messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : '100%',
    maxWidth: '100%',
    position: 'relative',
  },
  wideContainerInner: {
    flex: 1,
    position: 'relative',
    height: 'auto',
  },

  // Web waiting container
  waitingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 24,
    paddingVertical: 2,
    flexWrap: 'wrap',
  },
  webPetIcon: {
    marginRight: 8,
  },

  // ── Native-specific styles ──
  nativeContainer: {
    paddingVertical: 10,
    paddingHorizontal: 4,
  },
  nativeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  nativeIconContainer: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  nativeStatusTextContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  nativeDotsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 4,
  },
  nativeDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginHorizontal: 2,
  },
  // Shimmer bars
  nativeShimmerContainer: {
    gap: 8,
  },
  nativeShimmerBar: {
    height: 10,
    borderRadius: 5,
    overflow: 'hidden',
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// Native Bouncing Dots
// ─────────────────────────────────────────────────────────────────────────────

const NativeBouncingDots = React.memo(({ color }) => {
  const dot1 = useRef(new Animated.Value(0)).current;
  const dot2 = useRef(new Animated.Value(0)).current;
  const dot3 = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const createBounce = (dot, delay) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(delay),
          Animated.timing(dot, {
            toValue: -4,
            duration: 300,
            easing: Easing.out(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(dot, {
            toValue: 0,
            duration: 300,
            easing: Easing.in(Easing.ease),
            useNativeDriver: true,
          }),
          // Ensure each cycle has consistent duration across dots
          Animated.delay(600 - delay),
        ])
      );

    const animations = [
      createBounce(dot1, 0),
      createBounce(dot2, 150),
      createBounce(dot3, 300),
    ];
    Animated.parallel(animations).start();

    return () => animations.forEach(a => a.stop());
  }, [dot1, dot2, dot3]);

  return (
    <View style={nativeOnlyStyles.dotsRow}>
      {[dot1, dot2, dot3].map((dot, i) => (
        <Animated.View
          key={i}
          style={[
            nativeOnlyStyles.dot,
            { backgroundColor: color, transform: [{ translateY: dot }] },
          ]}
        />
      ))}
    </View>
  );
});
NativeBouncingDots.displayName = 'NativeBouncingDots';

// ─────────────────────────────────────────────────────────────────────────────
// Native Shimmer Bar  (opacity-based skeleton loading effect)
// ─────────────────────────────────────────────────────────────────────────────

const NativeShimmerBar = React.memo(({ width, delay, color }) => {
  const shimmer = useRef(new Animated.Value(0.25)).current;

  useEffect(() => {
    const anim = Animated.loop(
      Animated.sequence([
        Animated.delay(delay),
        Animated.timing(shimmer, {
          toValue: 0.55,
          duration: 900,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(shimmer, {
          toValue: 0.25,
          duration: 900,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ])
    );
    anim.start();
    return () => anim.stop();
  }, [shimmer, delay]);

  return (
    <Animated.View
      style={[
        nativeOnlyStyles.shimmerBar,
        {
          width,
          backgroundColor: color,
          opacity: shimmer,
        },
      ]}
    />
  );
});
NativeShimmerBar.displayName = 'NativeShimmerBar';

// ─────────────────────────────────────────────────────────────────────────────
// Native Thinking Indicator (full component)
// ─────────────────────────────────────────────────────────────────────────────

const formatStatusWithElapsed = (status, elapsedMs) => {
  if (!status || !Number.isFinite(elapsedMs) || elapsedMs < 10_000) return status;
  const totalSeconds = Math.floor(elapsedMs / 1000);
  if (totalSeconds < 60) return `${status} (${totalSeconds}s)`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${status} (${minutes}:${String(seconds).padStart(2, '0')})`;
};

// The backend only reports `elapsedMs` on execution heartbeats. Preprocessing
// emits a single status per phase, so a slow processor (product search hitting
// SerpAPI, deep research, file parsing) leaves the status line frozen on one
// phrase — and the rotating fallback phrases are suppressed as soon as any
// backend status exists. Tick locally from when the indicator appeared so the
// turn always visibly advances, and take whichever clock is further along.
const useLiveElapsedMs = (backendElapsedMs) => {
  const [startedAt] = useState(() => Date.now());
  const [localElapsedMs, setLocalElapsedMs] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setLocalElapsedMs(Date.now() - startedAt);
    }, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  return Number.isFinite(backendElapsedMs)
    ? Math.max(backendElapsedMs, localElapsedMs)
    : localElapsedMs;
};

const NativeThinkingIndicator = React.memo(({ inferenceStatus, inferenceStage, inferenceElapsedMs }) => {
  const { colors } = useTheme();
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const iconPulse = useRef(new Animated.Value(1)).current;
  const iconGlow = useRef(new Animated.Value(0.7)).current;
  const textOpacity = useRef(new Animated.Value(1)).current;
  // Latch last backend status so brief clears between thinking-token flushes
  // and keepalives don't snap back to "Starting your request…".
  const latchedStatusRef = useRef('');
  const latchedStageRef = useRef('');

  if (inferenceStatus) latchedStatusRef.current = inferenceStatus;
  if (inferenceStage) latchedStageRef.current = inferenceStage;

  const effectiveStatus = inferenceStatus || latchedStatusRef.current;
  const effectiveStage = inferenceStage || latchedStageRef.current;

  // Backend status
  const hasBackendStatus = Boolean(effectiveStatus || effectiveStage);
  const stageFallback = getStageFallback(effectiveStage);
  const elapsedMs = useLiveElapsedMs(inferenceElapsedMs);
  const displayStatus = formatStatusWithElapsed(effectiveStatus || stageFallback, elapsedMs);
  const statusText = hasBackendStatus ? displayStatus : WAITING_PHRASES[currentPhraseIndex];

  // Pulse icon
  useEffect(() => {
    const anim = Animated.loop(
      Animated.parallel([
        Animated.sequence([
          Animated.timing(iconPulse, { toValue: 1.15, duration: 800, useNativeDriver: true }),
          Animated.timing(iconPulse, { toValue: 1, duration: 800, useNativeDriver: true }),
        ]),
        Animated.sequence([
          Animated.timing(iconGlow, { toValue: 1, duration: 1000, useNativeDriver: true }),
          Animated.timing(iconGlow, { toValue: 0.7, duration: 1000, useNativeDriver: true }),
        ]),
      ])
    );
    anim.start();
    return () => anim.stop();
  }, [iconPulse, iconGlow]);

  // Rotate phrases
  useEffect(() => {
    if (hasBackendStatus) return;
    const interval = setInterval(() => {
      Animated.timing(textOpacity, { toValue: 0, duration: 180, useNativeDriver: true }).start(() => {
        setCurrentPhraseIndex(prev => (prev + 1) % WAITING_PHRASES.length);
        Animated.timing(textOpacity, { toValue: 1, duration: 180, useNativeDriver: true }).start();
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [textOpacity, hasBackendStatus]);

  const accentColor = colors.text_color_link || colors.solid_color_toast || '#6366f1';
  const shimmerColor = colors.solid_color_secondary;
  const brainFallback = (
    <Animated.View
      style={[
        nativeOnlyStyles.iconContainer,
        {
          backgroundColor: accentColor,
          transform: [{ scale: iconPulse }],
          opacity: iconGlow,
        },
      ]}
    >
      <Icon name="brain" size={14} color="#fff" />
    </Animated.View>
  );

  return (
    <View style={nativeOnlyStyles.container}>
      {/* Row: icon + status text + bouncing dots */}
      <View style={nativeOnlyStyles.row}>
        <PetWaitingIcon
          size={32}
          style={nativeOnlyStyles.petIcon}
          fallback={brainFallback}
        />

        <View style={nativeOnlyStyles.statusTextContainer}>
          <Animated.View style={{ opacity: hasBackendStatus ? 1 : textOpacity }}>
            <Text size="n" color="secondary" numberOfLines={1}>
              {statusText}
            </Text>
          </Animated.View>
          <NativeBouncingDots color={colors.text_color_tertiary} />
        </View>
      </View>

      {/* Shimmer skeleton bars */}
      <View style={nativeOnlyStyles.shimmerContainer}>
        <NativeShimmerBar width="92%" delay={0} color={shimmerColor} />
        <NativeShimmerBar width="78%" delay={200} color={shimmerColor} />
        <NativeShimmerBar width="60%" delay={400} color={shimmerColor} />
      </View>
    </View>
  );
});
NativeThinkingIndicator.displayName = 'NativeThinkingIndicator';

// Used by MessageContent once the assistant placeholder exists so native
// waiting UI stays consistent (icon + status + dots + shimmer) instead of
// swapping to bare centered dots.
export { NativeThinkingIndicator };

// Static native-only styles (no theme dependency for shapes)
const nativeOnlyStyles = StyleSheet.create({
  container: {
    paddingVertical: 10,
    paddingHorizontal: 4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14,
  },
  iconContainer: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  petIcon: {
    marginRight: 10,
  },
  statusTextContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  dotsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 6,
  },
  dot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    marginHorizontal: 2,
  },
  shimmerContainer: {
    gap: 8,
  },
  shimmerBar: {
    height: 10,
    borderRadius: 5,
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// Web WaitingDots (unchanged behavior)
// ─────────────────────────────────────────────────────────────────────────────

const WaitingDots = React.memo(({ 
  styles, 
  inferenceStatus, 
  inferenceStage, 
  inferenceElapsedMs,
}) => {
  const { colors } = useTheme();
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const textOpacity = useRef(new Animated.Value(1)).current;
  const ellipsisOpacity = useRef(new Animated.Value(0.4)).current;

  const hasBackendStatus = Boolean(inferenceStatus || inferenceStage);
  const stageFallback = getStageFallback(inferenceStage);
  const elapsedMs = useLiveElapsedMs(inferenceElapsedMs);
  const displayStatus = formatStatusWithElapsed(inferenceStatus || stageFallback, elapsedMs);

  useEffect(() => {
    if (hasBackendStatus) return;
    const interval = setInterval(() => {
      Animated.timing(textOpacity, { toValue: 0, duration: 200, useNativeDriver: true }).start(() => {
        setCurrentPhraseIndex(prev => (prev + 1) % WAITING_PHRASES.length);
        Animated.timing(textOpacity, { toValue: 1, duration: 200, useNativeDriver: true }).start();
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [textOpacity, hasBackendStatus]);

  useEffect(() => {
    const pulseAnimation = Animated.loop(
      Animated.sequence([
        Animated.timing(ellipsisOpacity, { toValue: 1, duration: 800, useNativeDriver: true }),
        Animated.timing(ellipsisOpacity, { toValue: 0.4, duration: 800, useNativeDriver: true }),
      ])
    );
    pulseAnimation.start();
    return () => pulseAnimation.stop();
  }, [ellipsisOpacity]);

  const statusText = hasBackendStatus ? displayStatus : WAITING_PHRASES[currentPhraseIndex];

  return (
    <View style={styles.waitingContainer}>
      <PetWaitingIcon size={34} style={styles.webPetIcon} />
      <Animated.View style={{ opacity: hasBackendStatus ? 1 : textOpacity, flexDirection: 'row', alignItems: 'center' }}>
        <Text size="n" color="secondary">
          {statusText}
        </Text>
        <Animated.Text style={{ opacity: ellipsisOpacity, color: colors.text_color_secondary, fontSize: 14, marginLeft: 2 }}>
          ...
        </Animated.Text>
      </Animated.View>
    </View>
  );
});
WaitingDots.displayName = 'WaitingDots';

// ─────────────────────────────────────────────────────────────────────────────
// MessageWaiting (entry point)
// On native: renders NativeThinkingIndicator with shimmer + icon + bouncing dots
// On web:    renders WaitingDots (pet + text + ellipsis)
// ─────────────────────────────────────────────────────────────────────────────

const MessageWaiting = React.memo(({ 
  inferenceStatus,
  inferenceStage,
  inferenceElapsedMs,
}) => {
  const { colors } = useTheme();
  const messageLayout = useUIStore(state => state.messageLayout);
  const { isXS } = useResponsiveness();
  const messageWidth = useUIStore(state => state.messageWidth);
  const styles = useMemo(() => createStyles(colors, isXS, messageWidth), [colors, isXS, messageWidth]);

  // Choose inner content based on platform
  const innerContent = isNative ? (
    <NativeThinkingIndicator
      inferenceStatus={inferenceStatus}
      inferenceStage={inferenceStage}
      inferenceElapsedMs={inferenceElapsedMs}
    />
  ) : (
    <WaitingDots
      styles={styles}
      inferenceStatus={inferenceStatus}
      inferenceStage={inferenceStage}
      inferenceElapsedMs={inferenceElapsedMs}
    />
  );

  if (messageLayout === MESSAGE_LAYOUT.BUBBLE) {
    return (
      <View style={styles.bubbleContainer}>
        <View style={styles.bubbleContainerInner}>
          {innerContent}
        </View>
      </View>
    );
  }

  if (messageLayout === MESSAGE_LAYOUT.WIDE) {
    return (
      <View style={styles.wideContainer}>
        <View style={styles.wideContainerInner}>
          {innerContent}
        </View>
      </View>
    );
  }

  // slim
  return (
    <View style={styles.slimContainer}>
      <View style={styles.slimContainerInner}>
        {!isNative && !isXS ? <View style={styles.slimAvatar} /> : null}
        <View style={styles.slimContentWrapper}>
          <View style={styles.slimContent}>
            {isNative ? (
              innerContent
            ) : (
              <>
                <View style={styles.slimContentHeader}>
                  <Text color='secondary' size='n' style={styles.slimContentUsername}>
                    {inferenceStatus || 'Inferencing...'}
                  </Text>
                  <Text color='tertiary' size='xs'>
                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Text>
                </View>
                <WaitingDots
                  styles={styles}
                  inferenceStatus={inferenceStatus}
                  inferenceStage={inferenceStage}
                  inferenceElapsedMs={inferenceElapsedMs}
                />
              </>
            )}
          </View>
        </View>
      </View>
    </View>
  );
});

MessageWaiting.propTypes = {
  inferenceStatus: PropTypes.string,
  inferenceStage: PropTypes.string,
  inferenceElapsedMs: PropTypes.number,
};

export default MessageWaiting;
